import { useMutation } from "@apollo/client";
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { sortBy } from "lodash";
import { useState } from "react";
import { graphql } from "~/gql";
import { mergeArraysOfObjectsByProp } from "~/helpers";

export const UPDATE_PARENT_MATTER = graphql(/* GraphQL */ `
  mutation UpdateParentMatter($updateInput: ParentMatterUpdateInput!, $updateParentMatterId: Int!) {
    updateParentMatter(updateInput: $updateInput, id: $updateParentMatterId) {
      id
      reference
      status
      client {
        id
        name
        externalReference
        externalId
        type
      }
      matters {
        id
        order
        reference
      }
    }
  }
`);

export const useParentOrder = <T extends { id: number; order?: number | null }>(
  parentPhaseId: number,
  parentMatterId: number,
  currentSortedItems: T[]
) => {
  const [updateParentMatter] = useMutation(UPDATE_PARENT_MATTER);

  const sensors = useSensors(useSensor(PointerSensor));
  const [orderedItems, setOrderedItems] = useState<T[]>(currentSortedItems);

  const onReOrder = (activeId: number, overId: number) => {
    const sortedItemsCopy =
      orderedItems.length > 0
        ? orderedItems.map((item) => ({ ...item }))
        : currentSortedItems.map((item) => ({ ...item }));

    const activeItem = sortedItemsCopy.find((d) => d.id === activeId);
    const overItem = sortedItemsCopy.find((d) => d.id === overId);

    const activeOrder = activeItem?.order!;
    const overOrder = overItem?.order!;

    const updates: T[] = [];
    // Drag Down
    if (activeOrder < overOrder) {
      const toShift = sortedItemsCopy
        .filter((x) => {
          const order = x.order ?? 0;
          return order <= overOrder && order > activeOrder;
        })
        .map((y) => ({ ...y, order: (y?.order ?? 0) - 1 }));
      updates.push({ ...activeItem!, order: overOrder });
      updates.push(...toShift);
    }
    //Drag Up
    else if (activeOrder > overOrder) {
      const toShift = sortedItemsCopy
        .filter((x) => {
          const order = x.order ?? 0;
          return order >= overOrder && order < activeOrder;
        })
        .map((y) => ({ ...y, order: (y?.order ?? 0) + 1 }));
      updates.push({ ...activeItem!, order: overOrder });
      updates.push(...toShift);
    }

    const joined = mergeArraysOfObjectsByProp(updates, sortedItemsCopy, "id", "asc", "order");

    updateParentMatter({
      variables: {
        updateParentMatterId: parentMatterId,
        updateInput: {
          phases: {
            update: [
              {
                data: {
                  matters: {
                    update: joined.map((x) => ({
                      data: { order: { set: x.order } },
                      where: { id: x.id },
                    })),
                  },
                },
                where: { id: parentPhaseId },
              },
            ],
          },
        },
      },
      onCompleted: () => {
        setOrderedItems(joined);
      },
    });
  };

  return {
    onReOrder,
    sensors,
    orderedItems:
      orderedItems.length > 0 ? orderedItems : sortBy(currentSortedItems, (x) => x.order),
  };
};
